import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { getUserProfile } from "../services/authAPI";

interface UserProfileData {
  // Add specific interface based on your API response
  [key: string]: any;
}

interface UseUserProfileReturn {
  userProfileData: UserProfileData | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useUserProfile = (accessToken: string): UseUserProfileReturn => {
  const [userProfileData, setUserProfileData] = useState<UserProfileData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserProfile = async (silent = false) => {
    if (!accessToken) return;
    
    if (!silent) {
      setLoading(true);
    }
    setError(null);

    try {
      const data = await getUserProfile(accessToken);
      const responseBody = typeof data.body === "string" ? JSON.parse(data.body) : data.body;

      if (data.statusCode === 200) {
        setUserProfileData(responseBody);
      } else if (data.statusCode === 400) {
        throw new Error("Bad Request");
      } else if (data.statusCode > 400 && data.statusCode < 420) {
        throw new Error("Unauthorized");
      } else if (data.statusCode >= 500) {
        throw new Error("Internal Server Error");
      }
    } catch (error: any) {
      const errorMessage = error.message || "Failed to fetch user profile";
      setError(errorMessage);
      if (!silent) {
        toast.error(errorMessage);
      }
      console.log("Authorization failed: " + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const refetch = async () => {
    await fetchUserProfile(false);
  };

  useEffect(() => {
    if (accessToken) {
      fetchUserProfile(false);
    }
  }, [accessToken]);

  return {
    userProfileData,
    loading,
    error,
    refetch,
  };
};